import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarClock,
  Check,
  Copy,
  FlaskConical,
  GitBranch,
  GitCommitHorizontal,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import {
  apiHref,
  fetchAssuranceDemo,
  fetchAssurancePortfolio,
  fetchRepositoryAssurance,
  normalizeAssessment,
  normalizePortfolio,
} from "../assurance/data";
import { selectDecision, selectScan, selectScanState } from "../assurance/model";
import type {
  AssuranceAssessment,
  AssuranceFinding,
  AssuranceLink,
} from "../assurance/types";
import { getAssuranceView } from "../assurance/views";
import {
  AssuranceRail,
  AssuranceViewIcon,
  AssuranceViewSwitcher,
} from "../components/assurance/AssuranceNavigation";
import AssuranceViewContent from "../components/assurance/AssuranceViews";
import {
  Pill,
  fmtDateTime,
  freshnessTone,
  relativeTime,
} from "../components/assurance/primitives";
import Layout from "../components/ui/Layout";
import { logActivity } from "../lib/activity";

interface AssuranceWorkspaceProps {
  demo?: boolean;
}

// ---------------------------------------------------------------------------
// Global states
// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading assurance evidence">
      <div className="asr-panel flex flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3">
        {[112, 90, 150, 120, 100].map((width, index) => (
          <div key={index} className="skeleton h-5 rounded-md" style={{ width }} />
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="asr-panel h-44 p-4">
            <div className="skeleton h-4 w-28 rounded" />
            <div className="skeleton mt-4 h-20 rounded-lg" />
          </div>
        ))}
      </div>
      <div className="asr-panel h-80 p-4">
        <div className="skeleton h-4 w-44 rounded" />
        <div className="mt-4 space-y-2.5">
          {Array.from({ length: 7 }).map((_, index) => (
            <div key={index} className="skeleton h-8 rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="asr-panel border-critical/25 p-8 text-center">
      <AlertTriangle className="mx-auto h-9 w-9 text-critical" aria-hidden="true" />
      <h2 className="mt-3 text-base font-semibold text-text">Assurance data unavailable</h2>
      <p className="mx-auto mt-1 max-w-xl text-sm leading-6 text-muted">{message}</p>
      <button type="button" onClick={onRetry} className="btn-primary mt-5">
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        Retry
      </button>
    </div>
  );
}

function SyntheticNotice({ notice }: { notice: string }) {
  return (
    <div
      className="mb-4 overflow-hidden rounded-xl border border-warning/40 bg-warning/10"
      data-assurance-synthetic="true"
      role="status"
    >
      <div className="flex items-start gap-3 p-4">
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-warning/15 text-warning ring-1 ring-warning/30">
          <FlaskConical className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-bold uppercase tracking-wide text-warning">
            Synthetic demonstration — not a production assessment
          </div>
          <p className="mt-1 text-xs leading-5 text-text2">
            {notice ||
              "All content on this route is synthetic demonstration data. It is not repository evidence, not a production assessment, not certification, not legal advice, and not an independently validated benchmark."}{" "}
            Values shown as "Not measured — requires validation" carry no
            evidentiary weight.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assurance context header
// ---------------------------------------------------------------------------

function CommitChip({ commit }: { commit: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(commit).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
      title={`Measured from commit ${commit} — click to copy`}
      className="inline-flex items-center gap-1.5 rounded-md bg-surface2/80 px-2 py-1 font-mono text-xs font-medium text-text2 ring-1 ring-border/60 transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
    >
      <GitCommitHorizontal className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
      {commit.slice(0, 7)}
      {copied ? (
        <Check className="h-3 w-3 text-success" aria-hidden="true" />
      ) : (
        <Copy className="h-3 w-3 text-muted" aria-hidden="true" />
      )}
    </button>
  );
}

function ContextHeader({
  assessment,
  demo,
  repositories,
  selectedRepoId,
  onSelectRepo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  repositories: { id: string; name: string }[];
  selectedRepoId?: string;
  onSelectRepo: (repoId: string) => void;
}) {
  const repository = assessment.repository;
  const decision = selectDecision(assessment);
  const scan = selectScan(assessment);
  const freshness = freshnessTone(assessment.generatedAt);

  const decisionMeta: Record<string, { label: string; tone: "critical" | "warning" | "success" | "neutral" }> = {
    blocked: { label: "Release blocked", tone: "critical" },
    review: { label: "Review required", tone: "warning" },
    pass: { label: "Gates passing", tone: "success" },
    unknown: {
      label: decision.total
        ? "Gates require validation"
        : demo
          ? "Requires validation"
          : "Not measured",
      tone: "neutral",
    },
  };
  const decisionInfo = decisionMeta[decision.state];

  return (
    <div className="asr-panel mb-4 flex flex-wrap items-center gap-x-5 gap-y-2.5 px-4 py-3">
      {/* Scope */}
      <div className="flex min-w-0 items-center gap-2.5">
        {!demo && repositories.length > 1 ? (
          <>
            <label htmlFor="assurance-repository" className="sr-only">
              Application repository
            </label>
            <select
              id="assurance-repository"
              className="input w-56 py-1.5 text-[13.5px] font-semibold"
              value={selectedRepoId}
              onChange={(event) => onSelectRepo(event.target.value)}
            >
              {repositories.map((repo) => (
                <option key={repo.id} value={repo.id}>
                  {repo.name}
                </option>
              ))}
            </select>
          </>
        ) : (
          <div className="min-w-0">
            <div className="truncate text-sm font-bold tracking-tight text-text">
              {repository?.organization ? `${repository.organization} / ` : ""}
              {repository?.name || "Repository"}
            </div>
            {repository?.provider && (
              <div className="truncate text-[11px] text-muted">{repository.provider}</div>
            )}
          </div>
        )}
      </div>

      <span className="hidden h-5 w-px bg-border sm:block" aria-hidden="true" />

      {/* Provenance */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted">
        {repository?.defaultBranch && (
          <span className="inline-flex items-center gap-1 font-mono">
            <GitBranch className="h-3.5 w-3.5" aria-hidden="true" />
            {repository.defaultBranch}
          </span>
        )}
        {repository?.commit && /^[0-9a-f]{7,64}$/i.test(repository.commit) && (
          <CommitChip commit={repository.commit} />
        )}
        {!demo && scan.scanId && (
          <span
            className="rounded-md bg-surface2/80 px-2 py-1 font-mono text-[11px] ring-1 ring-border/60"
            title={`Generated from scan ${scan.scanId}`}
          >
            Generated from scan {scan.scanId.slice(0, 8)}
          </span>
        )}
        {repository?.release && (
          <span className="rounded-md bg-surface2/80 px-2 py-1 font-mono text-[11.5px] ring-1 ring-border/60">
            {repository.release}
          </span>
        )}
      </div>

      <span className="flex-1" aria-hidden="true" />

      {/* Assessment state */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="inline-flex items-center gap-1.5 text-xs text-muted"
          title={`Assessed ${fmtDateTime(assessment.generatedAt) || "unknown"}`}
        >
          <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
          {fmtDateTime(assessment.generatedAt) || "Not measured"}
        </span>
        <Pill tone={freshness}>
          {relativeTime(assessment.generatedAt)
            ? `Evidence ${relativeTime(assessment.generatedAt)}`
            : "Freshness unknown"}
        </Pill>
        <Pill
          tone={decisionInfo.tone}
          icon={<ShieldCheck className="h-3 w-3" aria-hidden="true" />}
        >
          {decisionInfo.label}
        </Pill>
        <Pill tone={demo ? "warning" : "success"} icon={demo ? <FlaskConical className="h-3 w-3" aria-hidden="true" /> : undefined}>
          {demo ? "Synthetic data" : "Live repository evidence"}
        </Pill>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Endpoint links
// ---------------------------------------------------------------------------

function buildEndpointLinks(
  repoId: string | undefined,
  assessmentLinks: AssuranceLink[],
  demo: boolean
): AssuranceLink[] {
  const reportIds = [
    ["Overview report", "overview"],
    ["Executive report", "executive"],
    ["Exploitability report", "exploitability"],
    ["Technical debt report", "technical-debt"],
    ["Detailed assessment", "detailed"],
  ] as const;
  const knownLinks: AssuranceLink[] = demo
    ? [
        {
          label: "Assessment JSON",
          href: apiHref("/v1/assurance/demo/export.json"),
          kind: "json",
        },
        {
          label: "Findings CSV",
          href: apiHref("/v1/assurance/demo/export.csv"),
          kind: "csv",
        },
        {
          label: "CycloneDX SBOM",
          href: apiHref("/v1/assurance/demo/sbom.cyclonedx.json"),
          kind: "sbom",
        },
        {
          label: "SPDX SBOM",
          href: apiHref("/v1/assurance/demo/sbom.spdx.json"),
          kind: "sbom",
        },
        ...reportIds.map(([label, reportId]) => ({
          label,
          href: apiHref(`/v1/assurance/demo/report/${reportId}`),
          kind: "report",
        })),
      ]
    : repoId
      ? [
          {
            label: "Assurance JSON",
            href: apiHref(
              `/v1/assurance/repos/${encodeURIComponent(repoId)}/export.json`
            ),
            kind: "json",
          },
          {
            label: "Findings CSV",
            href: apiHref(
              `/v1/assurance/repos/${encodeURIComponent(repoId)}/export.csv`
            ),
            kind: "csv",
          },
          ...reportIds.map(([label, reportId]) => ({
            label,
            href: apiHref(
              `/v1/assurance/repos/${encodeURIComponent(repoId)}/report/${reportId}`
            ),
            kind: "report",
          })),
        ]
      : [];
  if (!demo && repoId) {
    knownLinks.push(
      {
        label: "CycloneDX SBOM",
        href: apiHref(
          `/v1/repos/${encodeURIComponent(repoId)}/sbom?format=cyclonedx`
        ),
        kind: "sbom",
      },
      {
        label: "SPDX SBOM",
        href: apiHref(`/v1/repos/${encodeURIComponent(repoId)}/sbom?format=spdx`),
        kind: "sbom",
      },
      {
        label: "SBOM CSV",
        href: apiHref(`/v1/repos/${encodeURIComponent(repoId)}/sbom?format=csv`),
        kind: "csv",
      }
    );
  }
  const relevantAssessmentLinks = assessmentLinks.filter((link) => {
    const kind = (link.kind || "").toLowerCase();
    const artifactKind = /^(report|export|sbom|json|csv)$/.test(kind);
    const artifactPath =
      link.href.startsWith("/") &&
      /(report|export|sbom|\.json(?:$|\?)|\.csv(?:$|\?))/i.test(link.href);
    return artifactKind || artifactPath;
  });
  const unique = new Map(
    [...knownLinks, ...relevantAssessmentLinks].map((link) => [
      `${link.label}|${link.href}`,
      link,
    ])
  );
  return Array.from(unique.values());
}

// ---------------------------------------------------------------------------
// Workspace
// ---------------------------------------------------------------------------

export default function AssuranceWorkspace({
  demo = false,
}: AssuranceWorkspaceProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = getAssuranceView(searchParams.get("view"));

  const demoQuery = useQuery({
    queryKey: ["assurance", "demo"],
    queryFn: fetchAssuranceDemo,
    enabled: demo,
  });
  const portfolioQuery = useQuery({
    queryKey: ["assurance", "portfolio"],
    queryFn: fetchAssurancePortfolio,
    enabled: !demo,
  });
  const repositories = useMemo(
    () => (portfolioQuery.data ? normalizePortfolio(portfolioQuery.data) : []),
    [portfolioQuery.data]
  );
  const requestedRepoId = searchParams.get("repo") || "";
  // Default: the repository the user last viewed (persisted), else the one
  // with the richest assurance evidence — most open findings, tie-broken by
  // most recent scan.
  const defaultRepoId = useMemo(() => {
    let remembered: string | null = null;
    try {
      remembered = window.localStorage.getItem("paxley-assurance-repo");
    } catch {
      remembered = null;
    }
    if (remembered && repositories.some((repo) => repo.id === remembered)) {
      return remembered;
    }
    const scanTime = (repo: { lastScanAt?: string | null }) =>
      repo.lastScanAt ? Date.parse(repo.lastScanAt) || 0 : 0;
    const best = repositories
      .slice()
      .sort(
        (a, b) =>
          (b.openFindings ?? 0) - (a.openFindings ?? 0) || scanTime(b) - scanTime(a)
      )[0];
    return best?.id || "";
  }, [repositories]);
  const selectedRepoId =
    requestedRepoId && repositories.some((repo) => repo.id === requestedRepoId)
      ? requestedRepoId
      : defaultRepoId;

  useEffect(() => {
    if (demo || !selectedRepoId) return;
    try {
      window.localStorage.setItem("paxley-assurance-repo", selectedRepoId);
    } catch {
      // Persistence is best-effort only.
    }
  }, [demo, selectedRepoId]);

  // Engagement beacon: which assurance view is open (never blocks navigation).
  useEffect(() => {
    if (demo) return;
    logActivity("assurance_view", { view: activeView, repo: selectedRepoId || null });
  }, [demo, activeView, selectedRepoId]);

  useEffect(() => {
    if (!demo && selectedRepoId && requestedRepoId !== selectedRepoId) {
      const next = new URLSearchParams(searchParams);
      next.set("repo", selectedRepoId);
      setSearchParams(next, { replace: true });
    }
  }, [demo, requestedRepoId, searchParams, selectedRepoId, setSearchParams]);

  const repositoryQuery = useQuery({
    queryKey: ["assurance", "repository", selectedRepoId],
    queryFn: () => fetchRepositoryAssurance(selectedRepoId),
    enabled: !demo && Boolean(selectedRepoId),
  });

  const assessment = useMemo<AssuranceAssessment | undefined>(() => {
    const envelope = demo ? demoQuery.data : repositoryQuery.data;
    if (!envelope) return undefined;
    const normalized = normalizeAssessment(envelope, demo ? "demo" : "live");
    return demo ? { ...normalized, mode: "demo" } : normalized;
  }, [demo, demoQuery.data, repositoryQuery.data]);

  const selectView = (view: string, params?: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", view);
    // View-scoped one-shot params (e.g. severity preset) never accumulate.
    next.delete("severity");
    for (const [key, value] of Object.entries(params || {})) {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const selectRepo = (repoId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("repo", repoId);
    next.delete("finding");
    setSearchParams(next);
  };

  const selectFinding = (finding: AssuranceFinding, openEvidence = false) => {
    const next = new URLSearchParams(searchParams);
    next.set("finding", finding.id);
    if (openEvidence) next.set("view", "evidence");
    setSearchParams(next);
  };

  const isLoading = demo
    ? demoQuery.isLoading
    : portfolioQuery.isLoading ||
      (Boolean(selectedRepoId) && repositoryQuery.isLoading);
  const error = demo
    ? demoQuery.error
    : portfolioQuery.error || repositoryQuery.error;
  const refetch = () => {
    if (demo) void demoQuery.refetch();
    else {
      void portfolioQuery.refetch();
      if (selectedRepoId) void repositoryQuery.refetch();
    }
  };
  const endpointLinks = assessment
    ? buildEndpointLinks(
        selectedRepoId || assessment.repository?.id,
        assessment.links,
        demo
      )
    : [];

  const navCounts = useMemo<Record<string, number>>(() => {
    if (!assessment) return {};
    return {
      findings: assessment.findings.length,
      ...(repositories.length ? { portfolio: repositories.length } : {}),
    };
  }, [assessment, repositories.length]);

  return (
    <Layout>
      <div className="flex min-h-dvh items-stretch">
        <AssuranceRail
          activeView={activeView.id}
          onSelect={selectView}
          counts={navCounts}
        />

        <div className="relative min-w-0 flex-1">
          <div
            aria-hidden="true"
            className="asr-glow pointer-events-none absolute inset-x-0 top-0 h-72"
          />
          <div className="relative max-w-[1800px] px-4 py-5 sm:px-6 2xl:px-8">
            {demo && <SyntheticNotice notice={demoQuery.data?.notice || ""} />}

            {/* Workspace header */}
            <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold tracking-tight text-text">
                  Application Assurance
                </h1>
                <div
                  aria-hidden="true"
                  className="mt-1.5 h-[3px] w-12 rounded-full bg-gradient-to-r from-brand to-focus/40"
                />
                <p className="mt-1.5 text-sm text-muted">
                  {demo
                    ? "Explore the full workspace on API-served synthetic data — nothing on this route is production evidence."
                    : "Evidence-backed assurance across security, engineering, delivery, and governance."}
                </p>
              </div>
              <button
                type="button"
                onClick={refetch}
                className="btn-secondary px-3 py-2 text-xs"
                disabled={isLoading}
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Refresh evidence
              </button>
            </header>

            <AssuranceViewSwitcher activeView={activeView.id} onSelect={selectView} />

            {isLoading ? (
              <LoadingState />
            ) : error ? (
              <ErrorState
                message={
                  error instanceof Error
                    ? error.message
                    : "The assurance API request failed."
                }
                onRetry={refetch}
              />
            ) : !demo && !repositories.length ? (
              <div className="asr-panel border-dashed p-10 text-center">
                <div className="text-base font-semibold text-text">
                  Let's scan your first application
                </div>
                <p className="mx-auto mt-1.5 max-w-md text-sm leading-6 text-muted">
                  You don't have any scanned applications yet. Paste a public
                  repository URL or upload your code as a zip — Paxley runs the
                  full analysis and the assurance evidence appears right here.
                </p>
                <Link to="/upload" className="btn-primary mt-5 inline-flex">
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  Scan a repository
                </Link>
              </div>
            ) : assessment ? (
              <>
                <ContextHeader
                  assessment={assessment}
                  demo={demo}
                  repositories={repositories}
                  selectedRepoId={selectedRepoId}
                  onSelectRepo={selectRepo}
                />

                {!demo && selectScanState(assessment) === "failed" && (
                  <div className="asr-panel mb-4 border-l-4 border-l-critical p-4" role="status">
                    <div className="text-sm font-semibold text-critical">
                      The latest scan failed.
                    </div>
                    <p className="mt-1 text-xs leading-5 text-text2">
                      Evidence below reflects persisted findings from earlier
                      completed scans. Run a new scan to refresh Assurance
                      evidence.
                    </p>
                  </div>
                )}
                {!demo && selectScanState(assessment) === "running" && (
                  <div className="asr-panel mb-4 border-l-4 border-l-info p-4" role="status">
                    <div className="text-sm font-semibold text-info">
                      A scan is currently running.
                    </div>
                    <p className="mt-1 text-xs leading-5 text-text2">
                      Assurance evidence will refresh when the scan completes.
                    </p>
                  </div>
                )}

                <div className="mb-4 flex items-start gap-3">
                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-brand/10 text-brand ring-1 ring-brand/20">
                    <AssuranceViewIcon viewId={activeView.id} className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
                      {activeView.group}
                    </div>
                    <h2 className="text-lg font-bold tracking-tight text-text">
                      {activeView.label}
                    </h2>
                    <p className="mt-0.5 text-[12.5px] leading-5 text-muted">
                      {activeView.description}
                    </p>
                  </div>
                </div>

                {!demo && selectScanState(assessment) === "none" ? (
                  <div className="asr-panel border-dashed p-10 text-center">
                    <div className="text-base font-semibold text-text">
                      No completed assessment is available for this repository.
                    </div>
                    <p className="mx-auto mt-1.5 max-w-md text-sm leading-6 text-muted">
                      Start a scan to generate Assurance evidence. Findings,
                      coverage, SBOM, and report artifacts appear here once the
                      scanner pipeline persists results for this repository.
                    </p>
                    <Link
                      to={`/dashboard/repos/${encodeURIComponent(selectedRepoId)}`}
                      className="btn-primary mt-5 inline-flex"
                    >
                      Start a scan
                    </Link>
                  </div>
                ) : (
                  <AssuranceViewContent
                    assessment={assessment}
                    view={activeView}
                    selectedFindingId={searchParams.get("finding") || undefined}
                    presetSeverity={searchParams.get("severity") || undefined}
                    onSelectFinding={selectFinding}
                    onSelectView={selectView}
                    endpointLinks={endpointLinks}
                    repositories={repositories}
                    selectedRepoId={selectedRepoId}
                    onSelectRepo={demo ? undefined : selectRepo}
                  />
                )}
              </>
            ) : (
              <div className="asr-panel border-dashed p-8 text-center">
                <div className="text-sm font-semibold text-text">
                  No repository assurance selected
                </div>
                <p className="mt-1 text-xs text-muted">
                  Select a repository returned by the portfolio endpoint to load its
                  live assurance evidence.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
